import { ingredientById } from "../data/ingredients";
import { localizedNameTemplate, localizedStepTemplate } from "./localize";
import { renderTemplate } from "./template";
import type {
  Candidate,
  IngredientOption,
  InstantiatedIngredient,
  InstantiatedRecipe,
  Locale,
  RecipeFamily,
  RecipeSlot,
} from "./types";

function optionFor(
  slot: RecipeSlot,
  ingredientId: string | undefined,
): IngredientOption | undefined {
  return slot.options.find((option) => option.ingredientId === ingredientId);
}

function isVeganOption(option: IngredientOption): boolean {
  return (
    ingredientById.get(option.ingredientId)?.dietTags.includes("vegan") ?? false
  );
}

export function fillRecipeSlots(
  recipe: RecipeFamily,
  candidate: Candidate,
  swaps: Record<string, string>,
  veganOnly: boolean,
): Map<string, IngredientOption> | null {
  const filled = new Map<string, IngredientOption>();

  for (const slot of recipe.slots) {
    const selectedIngredientId = candidate.slotAssignment[slot.id];
    const explicitSwapId = swaps[slot.id];
    let chosen = optionFor(slot, selectedIngredientId);

    if (selectedIngredientId && veganOnly && chosen && !isVeganOption(chosen))
      return null;
    if (!chosen && explicitSwapId) chosen = optionFor(slot, explicitSwapId);
    if (explicitSwapId && veganOnly && chosen && !isVeganOption(chosen))
      return null;
    if (!chosen) chosen = optionFor(slot, slot.canonicalIngredientId);

    if (
      !selectedIngredientId &&
      !explicitSwapId &&
      veganOnly &&
      chosen &&
      !isVeganOption(chosen)
    ) {
      chosen = slot.options
        .filter(isVeganOption)
        .sort(
          (a, b) =>
            b.editorialPriority - a.editorialPriority ||
            a.ingredientId.localeCompare(b.ingredientId),
        )[0];
    }

    if (!chosen || (veganOnly && !isVeganOption(chosen))) return null;
    filled.set(slot.id, chosen);
  }

  return filled;
}

export function instantiateRecipe(
  recipe: RecipeFamily,
  candidate: Candidate,
  selectedIngredientIds: string[],
  swaps: Record<string, string> = {},
  veganOnly = false,
  locale: Locale = "en",
): InstantiatedRecipe | null {
  const filled = fillRecipeSlots(recipe, candidate, swaps, veganOnly);
  if (!filled) return null;

  const selected = new Set(selectedIngredientIds);
  const values = new Map<
    string,
    {
      slot: RecipeSlot;
      ingredient: NonNullable<ReturnType<typeof ingredientById.get>>;
      option: IngredientOption;
    }
  >();
  const instantiatedIngredients: InstantiatedIngredient[] = [];

  for (const slot of recipe.slots) {
    const option = filled.get(slot.id);
    if (!option) return null;
    const ingredient = ingredientById.get(option.ingredientId);
    if (!ingredient) return null;

    values.set(slot.id, { slot, ingredient, option });
    const locked = selected.has(ingredient.id);
    const group = ingredient.assumedStaple
      ? "pantry"
      : locked
        ? "selected"
        : slot.required
          ? "add"
          : "optional";
    instantiatedIngredients.push({
      slotId: slot.id,
      slotLabel: slot.label,
      ingredient,
      option,
      group,
      locked,
    });
  }

  const allergens = [
    ...new Set(
      instantiatedIngredients.flatMap((item) => item.ingredient.allergens),
    ),
  ].sort();
  const vegan = instantiatedIngredients.every((item) =>
    item.ingredient.dietTags.includes("vegan"),
  );

  return {
    family: recipe,
    title: renderTemplate(
      localizedNameTemplate(recipe, locale),
      values,
      locale,
    ),
    ingredients: instantiatedIngredients,
    steps: recipe.steps.map((step) => ({
      id: step.id,
      text: renderTemplate(localizedStepTemplate(step, locale), values, locale),
    })),
    allergens,
    vegan,
  };
}

export function formatAmount(option: IngredientOption): string {
  return `${option.quantity} ${option.unit}`;
}
